import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { PhoneIncoming, PhoneOutgoing } from "lucide-react"
import { api } from "@/lib/api"
import { PageHeader } from "@/components/portal/PageHeader"
import { Loading, ErrorBox, fmtRelative } from "@/components/portal/LoadingError"
import { CreateContactModal } from "@/components/portal/CreateContactModal"
import { ContactEditModal } from "@/components/portal/ContactEditModal"
import { HeroLinkModal } from "@/components/portal/HeroLinkModal"
import { BookingModal } from "@/components/portal/BookingModal"
import { CallTaskModal } from "@/components/portal/CallTaskModal"
import { InterneAufgabeModal } from "@/components/portal/InterneAufgabeModal"
import { HeroInfoPopover } from "@/components/portal/HeroInfoPopover"
import { VORGANG_OHNE_NAME, vorgangBezeichnung } from "@/lib/vorgangName"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { cn } from "@/lib/utils"
import { anrufAufgabenText } from "@/lib/anrufAufgabe"
import { FolgeAktivitaetChips, OffenerAnrufHinweis, RueckmeldungMailChip } from "@/components/portal/OffenerAnrufHinweis"

type CallItem = NonNullable<Awaited<ReturnType<typeof fetchCalls>>>["items"][number] & {
  /** HERO-Bezeichnung des Vorgangs — `SAN-751` und `Badumbau` statt der
   *  nackten Nummer. Kommt aus dem lokalen Projekt-Cache und fehlt, solange
   *  der Vorgang dort noch nicht steht. Hier von Hand ergaenzt, weil
   *  `api-types.ts` generiert ist. */
  hero_project_nr?: string | null
  hero_project_title?: string | null
}

type ExtractedContact = {
  salutation: string
  first_name: string
  last_name: string
  email: string
  phone: string
  street: string
  zipcode: string
  city: string
  description: string
  project_name: string
}

async function fetchCalls(tenant: string) {
  const { data, error } = await api.GET("/api/v1/sipgate/calls", {
    params: { query: { tenant: tenant || undefined, limit: 200 } },
  })
  if (error) throw new Error(String(error))
  return data
}

type FilterKey = null | "missed" | "in" | "out" | "done"

/**
 * Wie der verknuepfte Vorgang heisst.
 *
 * 🔴 „Projekt #11717440" stand am 02.09.2026 in der Anrufliste, und der
 * Melder fragte zu Recht, was die Zuordnung eigentlich bringt: an einer
 * internen Nummer erkennt niemand, ob der richtige Vorgang verknuepft wurde.
 * HERO-Bezeichnungen statt IDs — kennt der Projekt-Cache den Vorgang noch
 * nicht, steht eine neutrale Beschriftung da, nie die Nummer (08.09.2026).
 */
function vorgangName(c: { hero_project_nr?: string | null; hero_project_title?: string | null }): string {
  return vorgangBezeichnung(c.hero_project_nr, c.hero_project_title) ?? VORGANG_OHNE_NAME
}

/**
 * Sipgate-Anruf-Liste — Card-Layout 1:1 wie admin/sipgate.py.
 * Stats-Pills oben (Alle/Verpasst/Eingehend/Ausgehend/Erledigt) filtern die Liste.
 * Channel-Tabs filtern zusaetzlich nach Kanal. Tenant-Gruppen als Sektionen.
 */
export default function SipgateCallsList() {
  const [tenant, setTenant] = useTenantFilter()
  const [filter, setFilter] = useState<FilterKey>(null)
  const [channel, setChannel] = useState<string>("")
  const qc = useQueryClient()
  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ["sipgate-calls", tenant],
    queryFn: () => fetchCalls(tenant),
  })

  const tenantsDd = useQuery({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => (await api.GET("/api/v1/tenants", {})).data,
    staleTime: 1000 * 60 * 5,
  })

  const all = data?.items || []
  const channels = useMemo(
    () => Array.from(new Set(all.map((c) => c.channel_name).filter(Boolean))).sort(),
    [all],
  )

  const stats = useMemo(() => {
    const f = channel ? all.filter((c) => c.channel_name === channel) : all
    return {
      all: f.length,
      missed: f.filter((c) => c.status === "missed").length,
      incoming: f.filter((c) => c.direction === "in" && c.status !== "missed").length,
      outgoing: f.filter((c) => c.direction === "out").length,
      done: f.filter((c) => c.status === "done").length,
    }
  }, [all, channel])

  const filtered = useMemo(() => {
    let f = all
    if (channel) f = f.filter((c) => c.channel_name === channel)
    if (filter === "missed") f = f.filter((c) => c.status === "missed")
    else if (filter === "done") f = f.filter((c) => c.status === "done")
    else if (filter === "in") f = f.filter((c) => c.direction === "in" && c.status !== "done" && c.status !== "missed")
    else if (filter === "out") f = f.filter((c) => c.direction === "out" && c.status !== "done")
    return f
  }, [all, channel, filter])

  // Gruppierung nach Tenant
  const groups = useMemo(() => {
    const m = new Map<string, CallItem[]>()
    for (const c of filtered) {
      const key = c.tenant_slug || "(ohne Tenant)"
      const arr = m.get(key) ?? []; arr.push(c); m.set(key, arr)
    }
    return Array.from(m.entries())
  }, [filtered])

  const toggleDone = useMutation({
    mutationFn: async ({ id, next }: { id: number; next: "done" | "answered" | "missed" }) => {
      const { error } = await api.PATCH("/api/v1/sipgate/calls/{call_id}", {
        params: { path: { call_id: id } },
        body: { status: next },
      })
      if (error) throw new Error(String(error))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sipgate-calls"] }),
  })

  if (isLoading) return <Loading label="Lädt Anrufe..." />
  if (error) return <div className="pp-main"><ErrorBox error={error} /></div>

  return (
    <>
      <PageHeader title="Sipgate Anrufe" count={stats.all} onRefresh={() => refetch()} refreshing={isFetching} />

      {/* Filter-Bar mit Stats-Pills */}
      <div className="pp-filter-bar">
        <select
          value={tenant}
          onChange={(e) => setTenant(e.target.value)}
          style={{ width: "auto" }}
        >
          <option value="">Alle Tenants</option>
          {tenantsDd.data?.items?.map((t) => (
            <option key={t.slug} value={t.slug}>{t.name}</option>
          ))}
          {/* voice#9: Marlenes eigener sipgate-Kanal (…8947, seit 10.09.2026 in sipgate „werkXpartner") — kein Mandant, eigene Gruppe. */}
          <option value="persoenlich">werkXpartner</option>
        </select>
        <div className="sg-stats">
          <button className={cn("sg-stat sg-stat-all", !filter && "active")} onClick={() => setFilter(null)}>Alle</button>
          <button className={cn("sg-stat sg-stat-missed", filter === "missed" && "active")} onClick={() => setFilter(filter === "missed" ? null : "missed")}>{stats.missed} Verpasst</button>
          <button className={cn("sg-stat sg-stat-in", filter === "in" && "active")} onClick={() => setFilter(filter === "in" ? null : "in")}>{stats.incoming} Eingehend</button>
          <button className={cn("sg-stat sg-stat-out", filter === "out" && "active")} onClick={() => setFilter(filter === "out" ? null : "out")}>{stats.outgoing} Ausgehend</button>
          <button className={cn("sg-stat sg-stat-done", filter === "done" && "active")} onClick={() => setFilter(filter === "done" ? null : "done")}>{stats.done} Erledigt</button>
        </div>
      </div>

      {/* Channel-Tabs */}
      {channels.length > 1 && (
        <div className="sg-ftabs">
          <button className={cn("sg-ftab", !channel && "sg-ftab-active")} onClick={() => setChannel("")}>Alle</button>
          {channels.map((ch) => (
            <button key={ch} className={cn("sg-ftab", channel === ch && "sg-ftab-active")} onClick={() => setChannel(ch === channel ? "" : ch)}>
              {ch}
            </button>
          ))}
        </div>
      )}

      {/* Tenant-Gruppen mit Cards */}
      <div id="sg-content">
        {groups.length === 0 ? (
          <div className="pp-card" style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>
            Keine Anrufe gefunden.
          </div>
        ) : (
          groups.map(([tenantSlug, items]) => {
            const offen = items.filter((c) => c.status !== "done").length
            return (
              <div key={tenantSlug} className="sg-tenant-group">
                <div className="sg-tenant-header">
                  <span>{tenantSlug === "(ohne Tenant)" ? "OHNE MANDANTEN-ZUORDNUNG"
                    : tenantSlug === "persoenlich" ? "WERKXPARTNER"
                    : tenantSlug.toUpperCase()}</span>
                  {offen > 0 && <span className="sg-count-badge">{offen} offen</span>}
                </div>
                {/* voice#9 (03.09.2026): Marlenes eigener sipgate-Kanal stand in
                    keiner Kanal-Tabelle — 17 Anrufe hingen an niemandem und waren
                    unter jedem Mandantenfilter unsichtbar. Jetzt kommen sie bei
                    jedem Filter mit, und die Zeile sagt, WARUM sie hier stehen
                    und wo man sie zuordnet. */}
                {tenantSlug === "(ohne Tenant)" && (
                  <div className="sg-hinweis" style={{ margin: "0 0 8px", display: "block" }}>
                    Die Rufnummer dieser Anrufe ist keinem Mandanten zugeordnet — deshalb kein
                    Kontaktabgleich und keine Zählung im Mandanten. Zuordnen: Mandant bearbeiten →
                    „Sipgate-Kanäle" → Nummer eintragen.
                  </div>
                )}
                {items.map((c) => (
                  <SipgateCard
                    key={c.id}
                    call={c}
                    onToggleDone={(next) => toggleDone.mutate({ id: c.id, next })}
                    pending={toggleDone.isPending}
                    betriebName={tenantsDd.data?.items?.find((t) => t.slug === c.tenant_slug)?.name}
                  />
                ))}
              </div>
            )
          })
        )}
      </div>
    </>
  )
}

function SipgateCard({
  call: c,
  onToggleDone,
  pending,
  betriebName,
}: {
  call: CallItem
  onToggleDone: (next: "done" | "answered" | "missed") => void
  pending: boolean
  /** #115: Name des Betriebs fuer die Sprecher-Beschriftung im Transkript. */
  betriebName?: string
}) {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [withProj, setWithProj] = useState(true)
  const [projectOnly, setProjectOnly] = useState(false)
  const [heroOpen, setHeroOpen] = useState(false)
  const [editContactOpen, setEditContactOpen] = useState(false)
  const [bookOpen, setBookOpen] = useState(false)
  const [taskOpen, setTaskOpen] = useState(false)
  const [internOpen, setInternOpen] = useState(false)
  const [summaryExpanded, setSummaryExpanded] = useState(false)
  const [transcriptOpen, setTranscriptOpen] = useState(false)
  const [extracted, setExtracted] = useState<ExtractedContact | null>(null)
  const [extracting, setExtracting] = useState(false)
  // #99: bei einer Bot-Weiterleitung zeigt Sipgate nur die Trunk-Nummer —
  // der echte Anrufer steht in transfer_anrufer.
  const phone = c.transfer_anrufer || (c.direction === "out" ? c.to : c.from)
  const isDone = c.status === "done"
  const aiSummary = (c as { ai_summary?: string | null }).ai_summary || ""
  const aiTranscription = (c as { ai_transcription?: string | null }).ai_transcription || ""

  const openCreate = async (opts: { withProject: boolean; projectOnly: boolean }) => {
    setWithProj(opts.withProject)
    setProjectOnly(opts.projectOnly)
    if ((aiTranscription || aiSummary) && !extracted) {
      setExtracting(true)
      try {
        const r = await fetch(`/api/v1/sipgate/calls/${c.id}/extracted-contact`, { credentials: "include" })
        if (r.ok) setExtracted(await r.json() as ExtractedContact)
      } catch { /* still open modal without extract */ }
      setExtracting(false)
    }
    setCreateOpen(true)
  }

  const unlink = async () => {
    if (!confirm("HERO-Verknüpfung trennen?")) return
    try {
      const res = await fetch(`/api/v1/sipgate/${c.id}/clear-hero-link`, { method: "POST", credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      qc.invalidateQueries({ queryKey: ["sipgate-calls"] })
    } catch (e) {
      alert("Fehler: " + (e instanceof Error ? e.message : String(e)))
    }
  }

  return (
    <div className={cn("sg-card", isDone && "sg-done", `status-${c.status}`)} data-id={c.id} data-status={c.status} data-direction={c.direction} data-channel={c.channel_name}>
      <div className="sg-top">
        <div className="sg-left">
          <div className="sg-avatar">
            {c.direction === "out" ? <PhoneOutgoing className="w-[18px] h-[18px] text-[#6366f1]" /> : <PhoneIncoming className="w-[18px] h-[18px] text-[#4f46e5]" />}
          </div>
          <div className="sg-info">
            <div className="sg-name-row">
              <span className="sg-name" data-pii>
                {c.hero_contact_name || phone}
              </span>
              <StatusBadgeSg status={c.status} />
              {c.channel_name && <span className="sg-badge-inline sg-badge-channel">{c.channel_name}</span>}
              {c.weiterleitung_id ? (
                <span className="sg-badge-inline sg-badge-transfer" title={c.transfer_grund || undefined}>
                  {istDirektWeiterleitung(c) ? "Direkt durchgestellt" : "KI-Bot hat durchgestellt"}
                  {c.weiterleitung_status === "missed" ? " · niemand abgenommen" : c.weiterleitung_status === "completed" ? " · angenommen" : ""}
                </span>
              ) : c.transfer_von_tenant ? (
                <span className="sg-badge-inline sg-badge-transfer" title={c.transfer_grund || undefined}>
                  {istDirektWeiterleitung(c) ? "Direkt-Weiterleitung von" : "Weiterleitung vom KI-Bot"} {c.transfer_von_tenant_name || c.transfer_von_tenant}
                </span>
              ) : c.bot_angenommen && (
                <span className="sg-badge-inline sg-badge-bot" title="Der KI-Bot hat das Gespräch geführt">KI-Bot</span>
              )}
            </div>
            {c.hero_contact_name && (
              <div className="sg-phone" data-pii>
                <a href={`tel:${phone}`}>{phone}</a>
              </div>
            )}
            {c.bot_angenommen && !c.transfer_von_tenant && !c.weiterleitung_id && (
              <div className="sg-transfer-hinweis">
                Der KI-Bot hat das Gespräch geführt.
                {" "}<Link to={`/ki-telefon?caller=${encodeURIComponent(phone)}`} onClick={(e) => e.stopPropagation()}>Bot-Gespräch ansehen</Link>
              </div>
            )}
            {(c.transfer_von_tenant || c.weiterleitung_id) && (
              <div className="sg-transfer-hinweis">
                {istDirektWeiterleitung(c)
                  ? "Am KI-Bot vorbei direkt an werkXpartner weitergeleitet (Rückruf nach verpasstem Anruf)."
                  : `Der KI-Bot hat den Anrufer an werkXpartner durchgestellt${c.transfer_grund ? ` — ${c.transfer_grund}` : ""}.`}
                {c.transfer_anrufer && c.voice_call_id && (
                  <> <Link to={`/ki-telefon?caller=${encodeURIComponent(c.transfer_anrufer)}`} onClick={(e) => e.stopPropagation()}>Bot-Gespräch ansehen</Link></>
                )}
                {c.weiterleitung_id && (
                  <> · <Link to={`/sipgate/${c.weiterleitung_id}`} onClick={(e) => e.stopPropagation()}>Weiterleitungs-Anruf</Link></>
                )}
              </div>
            )}
            {c.ai_headline && (
              <div data-pii style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 4, lineHeight: 1.4, fontWeight: 500 }}>{c.ai_headline}</div>
            )}
            {/* #86: wer war danach dran (Mail, Telefon) — sonst nur die Kundenmail */}
            {c.folge_aktivitaeten?.length
              ? <FolgeAktivitaetChips liste={c.folge_aktivitaeten} />
              : c.rueckmeldung_mail && <div><RueckmeldungMailChip mail={c.rueckmeldung_mail} /></div>}
            {/* #86: der neue Anruf zeigt, dass ein älterer noch offen ist */}
            {c.offener_anruf_davor && <OffenerAnrufHinweis anruf={c.offener_anruf_davor} variante="anruf" />}
            {aiSummary && (() => {
              const bullets = summaryToBullets(aiSummary)
              const visible = summaryExpanded ? bullets : bullets.slice(0, 3)
              const hasMore = !summaryExpanded && bullets.length > 3
              return (
                <ul data-pii style={{
                  fontSize: 12, color: "var(--text-secondary)", marginTop: 6, marginBottom: 0,
                  paddingLeft: 18, lineHeight: 1.5, listStyle: "disc",
                }}>
                  {visible.map((b, i) => <li key={i} style={{ marginBottom: 2 }}>{b}</li>)}
                  {hasMore && (
                    <li style={{ listStyle: "none", marginLeft: -18 }}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setSummaryExpanded(true) }}
                        style={{
                          background: "none", border: "none", padding: 0,
                          fontSize: 12, color: "var(--accent-color)", cursor: "pointer",
                        }}
                      >
                        + {bullets.length - 3} weitere
                      </button>
                    </li>
                  )}
                </ul>
              )
            })()}
            {aiTranscription && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setTranscriptOpen(true) }}
                style={{
                  background: "none", border: "none", padding: 0, marginTop: 4,
                  fontSize: 12, color: "var(--accent-color)", cursor: "pointer", textDecoration: "underline",
                }}
              >
                Transkript anzeigen
              </button>
            )}
          </div>
        </div>
        <div className="sg-right">
          <div className="sg-time">{fmtRelative(c.created_at)}</div>
          {(c.hero_contact_id || c.hero_project_id) && (
            <div className="sg-steps">
              <span className={cn("sg-dot", c.hero_contact_id && "done")}>K</span>
              <span className={cn("sg-dot", c.hero_project_id && "done", c.hero_contact_id && !c.hero_project_id && "next")}>P</span>
            </div>
          )}
          {(c.hero_contact_id || c.hero_project_id) && (
            <div className="sg-hero-links" data-pii>
              {c.hero_contact_id && (
                <HeroInfoPopover tenantSlug={c.tenant_slug || ""} contactId={c.hero_contact_id} projectId={c.hero_project_id}>
                  <a href={`/admin/open-hero/contact/${c.hero_contact_id}?tenant=${c.tenant_slug}`} target="_blank" onClick={(e) => e.stopPropagation()}>
                    {c.hero_contact_name || `Kontakt #${c.hero_contact_id}`}
                  </a>
                </HeroInfoPopover>
              )}
              {c.hero_contact_id && c.hero_project_id && " · "}
              {c.hero_project_id && (
                <HeroInfoPopover tenantSlug={c.tenant_slug || ""} contactId={c.hero_contact_id} projectId={c.hero_project_id}>
                  <a href={`/admin/open-hero/project/${c.hero_project_id}?tenant=${c.tenant_slug}`} target="_blank" onClick={(e) => e.stopPropagation()}>
                    {vorgangName(c)}
                  </a>
                </HeroInfoPopover>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="sg-actions">
        <div className="sg-actions-left">
          {c.hero_contact_id ? (
            <>
              <button type="button" className="sg-btn sg-btn-ghost" style={{ color: "var(--accent-color)" }}
                onClick={(e) => { e.stopPropagation(); setEditContactOpen(true) }}>
                Kontakt bearbeiten
              </button>
              <button type="button" className="sg-btn sg-btn-ghost" disabled={extracting}
                onClick={(e) => { e.stopPropagation(); openCreate({ withProject: false, projectOnly: false }) }}>
                {extracting ? "Lädt…" : "Neuer Kontakt"}
              </button>
              <button type="button" className="sg-btn sg-btn-ghost" style={{ color: "#dc2626" }}
                onClick={(e) => { e.stopPropagation(); unlink() }}>
                Trennen
              </button>
              {c.hero_project_id ? (
                <>
                  <a
                    href={`/admin/open-hero/project/${c.hero_project_id}?tenant=${encodeURIComponent(c.tenant_slug || "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="sg-btn sg-btn-ghost"
                    onClick={(e) => e.stopPropagation()}
                    title="Projekt in HERO öffnen"
                  >
                    Projekt bearbeiten
                  </a>
                  {!c.hero_booking_id ? (
                    <button type="button" className="sg-btn"
                      style={{ background: "#f59e0b", color: "#fff", borderColor: "#f59e0b" }}
                      onClick={(e) => { e.stopPropagation(); setBookOpen(true) }}>
                      Termin buchen
                    </button>
                  ) : (
                    <span
                      className="sg-badge-inline"
                      style={{ background: "#dcfce7", color: "#166534", border: "1px solid #86efac" }}
                      title={`HERO-Termin ID ${c.hero_booking_id}`}
                    >
                      ✓ {fmtBooking(c.booking_start_time, c.booking_end_time) || "Termin gebucht"}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <button type="button" className="sg-btn" disabled={extracting}
                    style={{ background: "#10b981", color: "#fff", borderColor: "#10b981" }}
                    onClick={(e) => { e.stopPropagation(); openCreate({ withProject: true, projectOnly: true }) }}>
                    {extracting ? "Lädt…" : "Projekt anlegen"}
                  </button>
                  <button type="button" className="sg-btn sg-btn-ghost"
                    onClick={(e) => { e.stopPropagation(); setHeroOpen(true) }}>
                    Projekt verknüpfen
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                className="sg-btn sg-btn-hero"
                disabled={extracting}
                onClick={(e) => { e.stopPropagation(); openCreate({ withProject: true, projectOnly: false }) }}
              >{extracting ? "Lädt…" : "Kontakt + Projekt"}</button>
              <button
                type="button"
                className="sg-btn sg-btn-ghost"
                disabled={extracting}
                onClick={(e) => { e.stopPropagation(); openCreate({ withProject: false, projectOnly: false }) }}
              >{extracting ? "Lädt…" : "Nur Kontakt"}</button>
              <button type="button" className="sg-btn sg-btn-ghost"
                onClick={(e) => { e.stopPropagation(); setHeroOpen(true) }}>
                {c.hero_project_id ? "Kontakt zuordnen" : "Zuordnen"}
              </button>
              {/* 🔑 „Es bringt ja nur etwas zuzuordnen, wenn man danach daraus
                  einen Termin buchen kann" (Paul, 02.09.2026). Genau das ging
                  hier nicht: ist ein Vorgang verknuepft, aber kein Kontakt,
                  fehlt der Buchen-Knopf — und die Zeile sagte nicht, warum.
                  Ein Termin haengt in HERO am Kontakt, also ist das der
                  naechste Schritt, und er steht jetzt da. */}
              {c.hero_project_id && (
                <span className="sg-hinweis" title="Ein Termin haengt in HERO am Kontakt">
                  Vorgang verknüpft — für einen Termin fehlt noch der Kontakt
                </span>
              )}
            </>
          )}
        </div>
        <div className="sg-actions-right">
          {!isDone ? (
            <button
              onClick={() => onToggleDone("done")}
              disabled={pending}
              className="sg-btn sg-btn-done"
            >
              Erledigt
            </button>
          ) : (
            <button
              onClick={() => onToggleDone(c.answered_at ? "answered" : "missed")}
              disabled={pending}
              className="sg-btn sg-btn-ghost"
              title="Als offen markieren"
            >
              ↺ Wieder öffnen
            </button>
          )}
          <button type="button" className="sg-btn sg-btn-ghost"
            onClick={(e) => { e.stopPropagation(); setTaskOpen(true) }}>
            Aufgabe
          </button>
          {/* #192: wie im Anruf-Detail (#39) — die interne Aufgabe bleibt im
              Portal (z. B. Rueckruf), die andere geht nach HERO. */}
          <button type="button" className="sg-btn sg-btn-ghost" title="Interne Aufgabe (bleibt im Portal)"
            onClick={(e) => { e.stopPropagation(); setInternOpen(true) }}>
            Interne Aufgabe
          </button>
        </div>
      </div>
      <CreateContactModal
        open={createOpen}
        onClose={() => { setCreateOpen(false); setProjectOnly(false) }}
        mode="sipgate"
        itemId={c.id}
        tenantSlug={c.tenant_slug}
        prefillPhone={extracted?.phone || phone || ""}
        prefillSalutation={extracted?.salutation || ""}
        prefillFirst={extracted?.first_name || ""}
        prefillLast={extracted?.last_name || ""}
        prefillEmail={extracted?.email || ""}
        prefillStreet={extracted?.street || ""}
        prefillZipcode={extracted?.zipcode || ""}
        prefillCity={extracted?.city || ""}
        prefillDescription={extracted?.description || ""}
        prefillProjectName={extracted?.project_name || ""}
        initialWithProject={withProj}
        projectOnly={projectOnly}
        onSuccess={() => { qc.invalidateQueries({ queryKey: ["sipgate-calls"] }) }}
      />
      <ContactEditModal
        open={editContactOpen}
        onClose={() => setEditContactOpen(false)}
        contactId={c.hero_contact_id ?? null}
        tenantSlug={c.tenant_slug || ""}
        invalidateKeys={[["sipgate-calls"]]}
      />
      <HeroLinkModal
        open={heroOpen}
        onClose={() => setHeroOpen(false)}
        mode="sipgate"
        itemId={c.id}
        tenantSlug={c.tenant_slug}
        currentContactId={c.hero_contact_id}
        currentContactName={c.hero_contact_name}
        currentProjectId={c.hero_project_id}
        invalidateKeys={[["sipgate-calls"]]}
      />
      <BookingModal
        open={bookOpen}
        onClose={() => setBookOpen(false)}
        emailId={c.id}
        mode="sipgate"
        defaultTitle={c.hero_contact_name || phone || ""}
        tenantSlug={c.tenant_slug}
        heroContactId={c.hero_contact_id}
        heroProjectId={c.hero_project_id}
        invalidateKeys={[["sipgate-calls"], ["calendar"]]}
      />
      <CallTaskModal
        open={taskOpen}
        onClose={() => setTaskOpen(false)}
        mode="sipgate"
        callId={c.id}
        tenantSlug={c.tenant_slug}
        defaultTitle={c.ai_headline || (c.hero_contact_name ? `Rückruf: ${c.hero_contact_name}` : `Rückruf: ${phone}`)}
        defaultComment={anrufAufgabenText({
          headline: c.ai_headline,
          summary: aiSummary,
          kontaktName: c.hero_contact_name,
          telefon: phone,
        })}
      />
      <InterneAufgabeModal
        open={internOpen}
        onClose={() => setInternOpen(false)}
        tenantSlug={c.tenant_slug || null}
        callId={c.id}
        titelVorschlag={c.ai_headline || (c.hero_contact_name ? `Rückruf: ${c.hero_contact_name}` : `Rückruf: ${phone}`)}
      />
      {transcriptOpen && (
        <TranscriptModal
          onClose={() => setTranscriptOpen(false)}
          title={`Transkript: ${c.hero_contact_name || phone}`}
          transcript={aiTranscription}
          direction={c.direction}
          betrieb={betriebName}
        />
      )}
    </div>
  )
}

/** Kürzt eine freie Zusammenfassung auf max 3 prägnante Bullets.
 * Erkennt bestehende Bullet-Zeichen (•, -, *) oder splittet sonst nach Sätzen.
 */
function summaryToBullets(text: string): string[] {
  const t = (text || "").trim()
  if (!t) return []
  // Wenn explizite Bullets vorhanden, nutzen
  const bulletLines = t.split(/\r?\n/).map((l) => l.trim()).filter((l) => /^[-•*]\s/.test(l))
  if (bulletLines.length >= 1) {
    return bulletLines.map((l) => l.replace(/^[-•*]\s+/, "").trim()).filter(Boolean)
  }
  // Sonst Satz-Split (berücksichtigt Abkürzungen grob)
  const sentences = t
    .replace(/\s+/g, " ")
    .split(/(?<=[.!?])\s+(?=[A-ZÄÖÜ])/)
    .map((s) => s.trim())
    .filter((s) => s.length > 3)
  return sentences
}

/** #99: Direkt-Weiterleitungen (Asterisk, am Bot vorbei) tragen den Grund
 *  „Direkt-Weiterleitung …" — sie sind keine Bot-Durchstellung. */
function istDirektWeiterleitung(c: { transfer_grund?: string | null }): boolean {
  return (c.transfer_grund || "").startsWith("Direkt-Weiterleitung")
}

type TranscriptLine = { speaker: "caller" | "callee" | "other"; text: string }

function parseTranscript(raw: string): TranscriptLine[] {
  if (!raw) return []
  const lines: TranscriptLine[] = []
  for (const line of raw.split(/\r?\n/)) {
    const t = line.trim()
    if (!t) continue
    // "Caller:", "Callee:", "[Caller]", "[Speaker 1]" etc.
    const m = t.match(/^(\[([^\]]+)\]|([A-Za-zäöüÄÖÜ0-9 _-]+?):)\s*(.*)$/)
    if (!m) {
      if (lines.length > 0) {
        // Fortsetzung der vorherigen Zeile
        lines[lines.length - 1].text += " " + t
      } else {
        lines.push({ speaker: "other", text: t })
      }
      continue
    }
    const rawSpeaker = (m[2] || m[3] || "").toLowerCase()
    const rest = m[4] || ""
    let speaker: TranscriptLine["speaker"] = "other"
    if (/caller|anrufer|kunde/.test(rawSpeaker)) speaker = "caller"
    else if (/callee|callcenter|agent|sanitec|büro|buero|team|sie|sanitär|sanitar/.test(rawSpeaker)) speaker = "callee"
    else if (/speaker\s*1|sprecher\s*1/.test(rawSpeaker)) speaker = "caller"
    else if (/speaker\s*2|sprecher\s*2/.test(rawSpeaker)) speaker = "callee"
    if (rest) lines.push({ speaker, text: rest })
  }
  return lines
}

/**
 * #115 (Marlene, 23.09.2026): „Im Sipgate-Transkript sind Anrufer und Sanitec
 * vertauscht." Sipgate beschriftet die Sprecher als Caller/Callee — technisch,
 * nicht nach Rolle. Bei einem AUSGEHENDEN Anruf (Rueckruf des Betriebs) ist
 * der Betrieb der Caller und der Kunde der Callee. Die Anzeige hing die Labels
 * aber fest an Caller = „Anrufer", Callee = „Sanitec" — bei jedem Rueckruf war
 * damit jede Sprechblase falsch beschriftet. Ausserdem stand „Sanitec" bei
 * allen Mandanten.
 *
 * Jetzt: die Richtung entscheidet, welche Seite der Kunde ist; der Betrieb
 * heisst so, wie er im Portal heisst.
 */
export function sprecherRollen(direction: string | null | undefined): { kunde: "caller" | "callee"; kundeLabel: string } {
  return direction === "out"
    ? { kunde: "callee", kundeLabel: "Kunde" }
    : { kunde: "caller", kundeLabel: "Anrufer" }
}

function TranscriptModal({
  onClose,
  title,
  transcript,
  direction,
  betrieb,
}: {
  onClose: () => void
  title: string
  transcript: string
  direction?: string | null
  betrieb?: string
}) {
  const lines = parseTranscript(transcript)
  const rollen = sprecherRollen(direction)
  const betriebLabel = (betrieb || "").trim() || "Betrieb"
  return (
    <div
      className="pp-overlay oben"
      data-portal-modal
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "40px 16px", overflowY: "auto",
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          width: "100%", maxWidth: 640, display: "flex", flexDirection: "column",
        }}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-[15px] font-semibold" style={{ margin: 0 }}>{title}</h2>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "var(--text-muted)" }}>×</button>
        </div>
        <div style={{
          padding: 18, overflowY: "auto", maxHeight: "70vh",
          background: "var(--bg-secondary, #F5F4F0)",
        }}>
          {lines.length === 0 ? (
            <div style={{ color: "var(--text-muted)", fontStyle: "italic", textAlign: "center", padding: 40 }}>
              Kein Transkript vorhanden
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {lines.map((l, i) => {
                // Kunde links/hell, Betrieb rechts/farbig — unabhaengig davon,
                // wer technisch Caller war.
                const isCaller = l.speaker !== "other" && l.speaker === rollen.kunde
                const isCallee = l.speaker !== "other" && l.speaker !== rollen.kunde
                const align = isCaller ? "flex-start" : isCallee ? "flex-end" : "center"
                const bg = isCaller ? "#fff" : isCallee ? "#6366f1" : "#e5e7eb"
                const color = isCallee ? "#fff" : "#2C2C2A"
                const label = isCaller ? rollen.kundeLabel : isCallee ? betriebLabel : "—"
                return (
                  <div key={i} style={{ display: "flex", justifyContent: align }}>
                    <div style={{
                      maxWidth: "75%", padding: "8px 12px", borderRadius: 12,
                      background: bg, color,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      fontSize: 13, lineHeight: 1.45,
                      borderBottomLeftRadius: isCaller ? 4 : 12,
                      borderBottomRightRadius: isCallee ? 4 : 12,
                    }}>
                      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
                      {l.text}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end">
          <button type="button" onClick={onClose} className="pp-btn pp-btn-sm">Schliessen</button>
        </div>
      </div>
    </div>
  )
}

function fmtBooking(start?: string | null, end?: string | null): string {
  if (!start) return ""
  try {
    const s = new Date(start)
    const datum = s.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" })
    const uhrS = s.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    if (!end) return `${datum} ${uhrS}`
    const e = new Date(end)
    const uhrE = e.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    return `${datum} ${uhrS}–${uhrE}`
  } catch {
    return ""
  }
}

function StatusBadgeSg({ status }: { status: string }) {
  const s = (status || "").toLowerCase()
  if (s === "missed") return <span className="sg-badge-inline sg-badge-missed">Verpasst</span>
  if (s === "done") return <span className="sg-badge-inline sg-badge-done">Erledigt</span>
  if (s === "answered") return <span className="sg-badge-inline sg-badge-answered">Angenommen</span>
  if (s === "ringing") return <span className="sg-badge-inline sg-badge-ringing">Klingelt</span>
  return <span className="sg-badge-inline">{status}</span>
}

